/* Shared cart transaction queue and stable line identity. Keys/positions can change with discounts. */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory;
  else root.PMCartOperations = root.PMCartOperations || factory({
    fetch: (...args) => root.fetch(...args),
    root: () => root.Shopify?.routes?.root || '/',
    locks: root.navigator?.locks
  });
})(typeof window === 'undefined' ? globalThis : window, function (ports) {
  'use strict';
  let tail = Promise.resolve();
  function identity(item) {
    const entries = Array.isArray(item.properties) ? item.properties : Object.entries(item.properties || {});
    const properties = entries.filter(([, v]) => v !== null && v !== '')
      .map(([key, value]) => [key, String(value)]).sort(([a], [b]) => a.localeCompare(b));
    return JSON.stringify([String(item.variant_id || item.id), properties,
      String(item.selling_plan_allocation?.selling_plan?.id || item.sellingPlanId || '')]);
  }
  function fromRow(row) {
    // Missing identity must fail closed rather than guess the first matching variant.
    if (!row?.hasAttribute('data-line-properties')) throw new Error('Your cart changed. Please refresh it and try again.');
    return { key: row.dataset.lineKey, variant_id: row.dataset.variantId,
      properties: JSON.parse(row.dataset.lineProperties), sellingPlanId: row.dataset.sellingPlanId || '' };
  }
  function resolve(cart, target) {
    const matches = cart.items.filter(item => identity(item) === identity(target));
    const exact = matches.find(item => item.key === target.key);
    if (exact) return exact;
    if (matches.length > 1) throw new Error('Your cart changed. Please refresh it and try again.');
    return matches[0] || null;
  }
  function run(task) {
    const result = tail.then(() => ports.locks ? ports.locks.request('pm-gwp-cart', task) : task());
    tail = result.catch(() => {});
    return result;
  }
  async function readResponse(response) {
    let data;
    try { data = JSON.parse(await response.text()); }
    catch (_) { const error = new Error('cart-change:non-json-response'); error.silent = true; throw error; }
    if (!response.ok) {
      const message = data.description || data.errors || data.message || String(response.status);
      const error = new Error(typeof message === 'string' ? message : JSON.stringify(message));
      error.status = response.status;
      if (response.status >= 500) error.silent = true;
      throw error;
    }
    return data;
  }
  async function request(url, options) {
    const abort = new AbortController();
    const timeout = setTimeout(() => abort.abort(), 12000);
    // Local metadata only (not an HTTP header/body field): the gift observer must
    // not treat requests inside our own transaction as a new external cart edit.
    try { return await readResponse(await ports.fetch(url, { ...options, signal: abort.signal, [Symbol.for('pm.cart.operation')]: true })); }
    finally { clearTimeout(timeout); }
  }
  function readCart() {
    const root = ports.root().replace(/\/?$/, '/');
    return request(root + 'cart.js', { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' } });
  }
  async function readSections(ids, pathname) {
    // On /cart and /products/*, Accept: application/json selects cart/product JSON
    // instead of Section Rendering, even when the sections query is present.
    const data = await request(pathname + '?sections=' + encodeURIComponent(ids.join(',')), {
      credentials: 'same-origin', cache: 'no-store', headers: { Accept: '*/*' }
    });
    if (!data || ids.some(id => !Object.prototype.hasOwnProperty.call(data, id) ||
        (data[id] !== null && typeof data[id] !== 'string'))) {
      const error = new Error('cart-refresh:invalid-sections-response'); error.silent = true; throw error;
    }
    return data;
  }
  async function change(target, quantity, options = {}) {
    // Caller owns run() for the entire read/write/render transaction. No nested lock.
    if (!Number.isSafeInteger(quantity) || quantity < 0) throw new Error('Please enter a valid quantity.');
    const root = ports.root().replace(/\/?$/, '/');
    for (let attempt = 0; attempt < 2; attempt++) {
      const before = await readCart();
      const item = resolve(before, target);
      if (!item) return { data: before, before, item: null }; // Already removed: never fall back to its old index.
      try {
        const data = await request(root + 'cart/change.js', {
          method: 'POST', credentials: 'same-origin', headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...options, id: item.key, quantity })
        });
        return { data, before, item };
      } catch (error) {
        // Only a rejected stale-key request is safe to retry. Never replay an ambiguous timeout/5xx.
        if (attempt || ![400, 422].includes(error.status) || !/no valid id or line|line item.*(?:not found|does not exist)/i.test(error.message)) throw error;
      }
    }
  }
  return { identity, fromRow, resolve, run, change, readCart, readSections };
});
